import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, PenSquare } from "lucide-react";
import { fadeUp, fadeUpStagger, IN_VIEW } from "@/lib/motion";
import { GBP_PLACE_URL, GBP_RATING, GBP_REVIEW_COUNT } from "@/lib/contact";
import { track } from "@/lib/analytics";

/**
 * Reviews section — 100% based on the REAL, publicly verifiable Google
 * Business Profile data (rating + review count read live from Google).
 * NO invented quotes, NO invented names, NO invented aggregate.
 * When new reviews arrive, only bump GBP_RATING and GBP_REVIEW_COUNT
 * in /src/lib/contact.js.
 */
export default function Reviews() {
  const ratingText = Number.isInteger(GBP_RATING)
    ? `${GBP_RATING}.0`
    : String(GBP_RATING);
  const reviewsWord = GBP_REVIEW_COUNT === 1 ? "recensione" : "recensioni";

  return (
    <section
      id="recensioni"
      data-testid="reviews-section"
      className="relative py-20 md:py-28 bg-navy text-white overflow-hidden grain"
    >
      <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-brand-orange/15 blur-[140px] pointer-events-none" />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={IN_VIEW}
          variants={fadeUpStagger}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.p
            variants={fadeUp}
            className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase"
          >
            — 05 / Recensioni
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            <span className="text-brand-orange italic">{ratingText}</span> su Google.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed"
          >
            {GBP_REVIEW_COUNT} {reviewsWord} verificate sul nostro profilo Google Business.
            Solo dati reali: nessuna recensione inventata, nessun numero gonfiato.
          </motion.p>

          {/* Real rating badge */}
          <motion.div
            variants={fadeUp}
            className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white/5 ring-1 ring-white/15 px-5 py-4 backdrop-blur-sm"
            data-testid="reviews-badge"
          >
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-white text-navy font-display font-black text-lg">
              G
            </span>
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.round(GBP_RATING)
                        ? "fill-brand-orange text-brand-orange"
                        : "text-white/30"
                    }`}
                  />
                ))}
                <span className="ml-2 font-display font-bold text-white">
                  {ratingText} / 5
                </span>
              </div>
              <div className="text-white/70 text-xs mt-0.5">
                {GBP_REVIEW_COUNT} {reviewsWord} Google verificate
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              href={GBP_PLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window !== "undefined" && typeof window.gtag === "function") {
                  window.gtag("event", "google_reviews_view", { location: "reviews_section" });
                }
              }}
              data-testid="reviews-view-google-button"
              className="inline-flex items-center gap-2 rounded-2xl bg-white text-navy px-6 py-3.5 font-bold hover:brightness-95 hover:-translate-y-0.5 transition"
            >
              <ExternalLink className="h-4 w-4" />
              Vedi le recensioni su Google
            </a>
            <a
              href={GBP_PLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window !== "undefined" && typeof window.gtag === "function") {
                  window.gtag("event", "google_reviews_write", { location: "reviews_section" });
                }
                track && null;
              }}
              data-testid="reviews-write-google-button"
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-orange text-white px-6 py-3.5 font-bold hover:brightness-110 hover:-translate-y-0.5 transition btn-glow-orange"
            >
              <PenSquare className="h-4 w-4" />
              Lascia una recensione
            </a>
          </motion.div>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-white/40 text-xs font-mono tracking-widest uppercase"
          >
            Dati aggiornati dal profilo Google · {new Date().getFullYear()}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
